import { useEffect, useRef } from 'react';
import { cn } from '../../utils/cn';
import type { Tone } from './toneStyles';

export const TOAST_DEFAULT_DURATION_MS = 2200;

export interface ToastProps {
    message: string;
    tone?: Tone;
    /** Milliseconds before auto-dismiss. */
    duration?: number;
    onDismiss: () => void;
}

/**
 * A single toast pill. Auto-dismisses after `duration`, pausing the
 * countdown while the pointer is over it and resuming the remaining time on
 * pointer-leave. Dismissible on click.
 */
export function Toast({ message, tone = 'neutral', duration = TOAST_DEFAULT_DURATION_MS, onDismiss }: ToastProps) {
    const remainingRef = useRef(duration);
    const startedAtRef = useRef<number>(0);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const onDismissRef = useRef(onDismiss);
    onDismissRef.current = onDismiss;

    useEffect(() => {
        remainingRef.current = duration;
        start();
        return clear;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [duration]);

    function clear() {
        if (timeoutRef.current !== null) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }

    function start() {
        clear();
        startedAtRef.current = Date.now();
        timeoutRef.current = setTimeout(() => onDismissRef.current(), remainingRef.current);
    }

    function pause() {
        clear();
        remainingRef.current = Math.max(0, remainingRef.current - (Date.now() - startedAtRef.current));
    }

    const isDanger = tone === 'danger';

    return (
        <div
            role="status"
            aria-live={isDanger ? 'assertive' : 'polite'}
            onMouseEnter={pause}
            onMouseLeave={start}
            onClick={onDismiss}
            className={cn(
                'pointer-events-auto cursor-pointer select-none',
                'bg-surface-panel border border-hairline-strong rounded-full px-4 py-2',
                'text-value text-content-primary shadow-e3 animate-pop-in',
                isDanger && 'border-status-danger',
            )}
        >
            {message}
        </div>
    );
}

export default Toast;
